import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'

interface IPreviewTitle {
  section: 'MAIN' | 'TRADE' | 'FEED' | 'SHOP'
}

const PreviewImage: React.FC<IPreviewTitle> = ({ section }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const getImagesForSection = (section: string) => {
    switch (section) {
      case 'MAIN':
        return [
          {
            src: '/images/main/offpan&pocapan/Section_screen_pocapan.png',
            alt: 'preview_pocapan'
          },
          {
            src: '/images/main/offpan&pocapan/Section_screen_offpan.png',
            alt: 'preview_offpan'
          }
        ]
      case 'TRADE':
        return [
          {
            src: '/images/main/trade/Section_screen_trade01.png',
            alt: 'preview_trade01'
          },
          {
            src: '/images/main/trade/Section_screen_trade02.png',
            alt: 'preview_trade02'
          },
          {
            src: '/images/main/trade/Section_screen_trade03.png',
            alt: 'preview_trade03'
          }
        ]
      default:
        return []
    }
  }

  const images = getImagesForSection(section)

  useEffect(() => {
    if (section !== 'SHOP' && images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length)
      }, 6000)

      return () => clearInterval(interval)
    }
  }, [section, images.length])

  const renderAnimatedImages = () => (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentImageIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="absolute inset-0"
      >
        <Image
          src={images[currentImageIndex].src}
          alt={images[currentImageIndex].alt}
          fill
          priority
        />
      </motion.div>
    </AnimatePresence>
  )

  const feedContainerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.5
      }
    }
  }
  const feedItemVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 1
      }
    }
  }

  switch (section) {
    case 'MAIN':
      return (
        <div className="relative mx-auto flex h-[480px] items-center justify-center overflow-hidden sm:h-[604px] sm:w-[558px]">
          <Image
            src="/images/main/offpan&pocapan/PreviewBackground.png"
            alt="preview-background"
            fill
            className="absolute inset-0 z-0 object-contain"
          />
          <div className="absolute z-20 flex h-[410px] w-[210px] sm:h-[513px] sm:w-[250px]">
            {renderAnimatedImages()}
          </div>
          <div className="absolute z-10 flex h-[410px] w-[210px] sm:h-[513px] sm:w-[250px]">
            <Image
              src="/images/main/Section_screen_empty.png"
              alt="preview_empty"
              fill
              priority
            />
          </div>
        </div>
      )
    case 'TRADE':
      return (
        <div className="relative mx-auto flex h-[480px] items-center justify-center overflow-hidden sm:h-[604px] sm:w-[558px]">
          <Image
            src="/images/main/trade/PreviewBackground.png"
            alt="preview-background"
            fill
            className="absolute inset-0 z-0 object-contain"
          />
          <div className="absolute z-20 flex h-[410px] w-[210px] sm:h-[513px] sm:w-[250px]">
            {renderAnimatedImages()}
          </div>
          <div className="absolute z-10 flex h-[410px] w-[210px] sm:h-[513px] sm:w-[250px]">
            <Image
              src="/images/main/Section_screen_empty.png"
              alt="preview_empty"
              fill
              priority
            />
          </div>
        </div>
      )
    case 'FEED':
      return (
        <motion.div
          className="relative mx-auto flex h-[480px] items-center justify-center overflow-hidden sm:h-[604px] sm:w-[558px]"
          variants={feedContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.7 }}
        >
          <Image
            src="/images/main/feed/PreviewBackground.png"
            alt="preview-background"
            fill
            className="absolute z-0 object-contain"
          />
          <motion.div
            variants={feedItemVariants}
            className="absolute left-1/2 top-1/2 z-10 flex h-[185px] w-[186px] -translate-y-[60px] translate-x-[-218px] opacity-80 sm:left-[-20px] sm:top-[220px] sm:h-[226px] sm:w-[254px] sm:-translate-y-0 sm:translate-x-0"
          >
            <Image
              src="/images/main/feed/Section_screen_feed_item_01.png"
              alt="preview_feed_item_01"
              fill
              priority
            />
          </motion.div>
          <div className="absolute z-20 flex h-[410px] w-[210px] sm:h-[513px] sm:w-[250px]">
            <Image
              src="/images/main/feed/Section_screen_feed.png"
              alt="preview_feed"
              fill
              priority
            />
          </div>
          <motion.div
            variants={feedItemVariants}
            className="absolute right-1/2 top-1/2 z-30 flex h-[83px] w-[183px] translate-x-[155px] translate-y-[-85px] sm:right-[94px] sm:top-[184px] sm:h-[105px] sm:w-[228px] sm:translate-x-0 sm:translate-y-0"
          >
            <Image
              src="/images/main/feed/Section_screen_feed_item_02.png"
              alt="preview_feed_item_02"
              fill
              priority
            />
          </motion.div>
          <motion.div
            variants={feedItemVariants}
            className="absolute right-1/2 top-1/2 z-30 flex h-[96px] w-[205px] translate-x-[62px] translate-y-[38px] sm:right-[230px] sm:top-[340px] sm:h-[126px] sm:w-[257px] sm:translate-x-0 sm:translate-y-0"
          >
            <Image
              src="/images/main/feed/Section_screen_feed_item_03.png"
              alt="preview_feed_item_03"
              fill
              priority
            />
          </motion.div>
        </motion.div>
      )

    case 'SHOP':
      return (
        <div className="relative mx-auto flex h-[480px] items-center justify-center overflow-hidden sm:h-[604px] sm:w-[558px]">
          <Image
            src="/images/main/shop/PreviewBackground.png"
            alt="preview-background"
            fill
            className="absolute inset-0 z-0 object-contain"
          />
          <div className="absolute z-10 flex h-[410px] w-[210px] sm:h-[513px] sm:w-[250px]">
            <Image
              src="/images/main/shop/Section_screen_shop.png"
              alt="preview_commerce"
              fill
              priority
            />
          </div>
        </div>
      )
  }
}

export default PreviewImage
